/**
 * TanStack Query hooks for SubContractorHub backfill orchestration (SCH-3.2).
 *
 * Five hooks:
 *   - useStartBackfill()      — superadmin mutation
 *   - useSyncProgress(id)     — polling query (3s while active)
 *   - useContinueBackfill()   — superadmin mutation
 *   - useActiveBackfill()     — read-only query (used for Resume banner)
 *   - useCanRunBackfill()     — boolean: is this user allowed to start/
 *     continue/resume a backfill? (Admins via `manage_settings` + Super
 *     Admins via `system.override`, matching the backend's
 *     get_current_settings_manager gate)
 *
 * The useCanRunBackfill helper is centralized here so the gate is a one-line
 * change. It was superadmin-only at V1 launch (SCH-3.2 Dev Note "Why backfill
 * is superadmin-only at V1 launch") and relaxed to tenant Admins once the
 * feature was validated.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useHasAnyPermission } from '@/contexts/PermissionContext';
import { supabase } from '@/lib/supabaseClient';
import { backfillProgressService } from '@/services/integrations/backfillProgressService';
import type {
  ActiveBackfillResponse,
  BackfillMutationResult,
  ContinueBackfillResponse,
  StartBackfillResponse,
  SyncProgressResponse,
} from '@/types/integrations/backfillProgress';

const SYNC_PROGRESS_KEY = ['sch', 'sync-progress'] as const;
const ACTIVE_BACKFILL_KEY = ['sch', 'active-backfill'] as const;

/**
 * Boolean: is this user allowed to start / continue / resume a backfill?
 *
 * Gated to tenant Admins (`manage_settings`) and Super Admins
 * (`system.override`) — the same permission set that guards access to the
 * Settings → Integrations area itself (see `SettingsGuard` in
 * PermissionGuard.tsx). Matches the backend's
 * `Depends(get_current_settings_manager)` on the tenant-scoped operational
 * endpoints. The original V1 launch restricted this to superadmin-only while
 * the feature was validated (see SCH-3.2 Dev Note); that gate was relaxed once
 * backfill was proven stable.
 */
export function useCanRunBackfill(): {
  canRun: boolean;
  loading: boolean;
} {
  const { hasPermission, loading } = useHasAnyPermission([
    'manage_settings',
    'system.override',
  ]);
  return { canRun: hasPermission, loading };
}

export interface StartBackfillOptions {
  batchSize?: number;
  /** POC import mode — restrict the run to specific SCH project uuids. */
  subhubUuidFilter?: string[];
  /**
   * SCH-3.9 auto-continue mode — when true, the backend chains batches
   * automatically until catalog_exhausted, paused, or the integration is
   * disabled. Default false preserves the gated Continue-between-batches flow.
   */
  autoContinue?: boolean;
}

export function useStartBackfill() {
  const queryClient = useQueryClient();
  return useMutation<
    BackfillMutationResult<StartBackfillResponse>,
    Error,
    StartBackfillOptions | undefined
  >({
    mutationFn: (options?: StartBackfillOptions) =>
      backfillProgressService.startBackfill(options ?? {}),
    onSuccess: (result) => {
      if (result.data) {
        // Seed the active-backfill cache so the panel's Resume banner
        // renders immediately (no extra round-trip).
        queryClient.invalidateQueries({ queryKey: ACTIVE_BACKFILL_KEY });
        queryClient.invalidateQueries({
          queryKey: [...SYNC_PROGRESS_KEY, result.data.syncRunId],
        });
      }
    },
    retry: false,
  });
}

export interface ContinueBackfillVariables {
  syncRunId: string;
  /**
   * SCH-3.9: when true, this click upgrades the run to auto-continue mode
   * for the remainder. Default false preserves the gated single-batch
   * Continue behavior.
   */
  autoContinue?: boolean;
}

export function useContinueBackfill() {
  const queryClient = useQueryClient();
  return useMutation<
    BackfillMutationResult<ContinueBackfillResponse>,
    Error,
    string | ContinueBackfillVariables
  >({
    mutationFn: (variables) => {
      // Accept either a bare syncRunId string (legacy call site) OR a
      // ContinueBackfillVariables object (preferred new shape).
      if (typeof variables === 'string') {
        return backfillProgressService.continueBackfill(variables);
      }
      return backfillProgressService.continueBackfill(variables.syncRunId, {
        autoContinue: variables.autoContinue,
      });
    },
    onSuccess: (result, variables) => {
      const syncRunId =
        typeof variables === 'string' ? variables : variables.syncRunId;
      if (result.data) {
        queryClient.invalidateQueries({
          queryKey: [...SYNC_PROGRESS_KEY, syncRunId],
        });
      }
    },
    retry: false,
  });
}

/**
 * SCH-4.4: request abort for an in-flight backfill run. Writes a marker
 * row on the backend; the orchestrator picks it up between iterations and
 * exits cleanly with paused_reason='aborted_by_user' (within ~30-60s).
 */
export function useAbortBackfill() {
  const queryClient = useQueryClient();
  return useMutation<
    BackfillMutationResult<{ sync_run_id: string; status: string }>,
    Error,
    string
  >({
    mutationFn: (syncRunId: string) =>
      backfillProgressService.abortBackfill(syncRunId),
    onSuccess: (result, syncRunId) => {
      if (result.data) {
        queryClient.invalidateQueries({
          queryKey: [...SYNC_PROGRESS_KEY, syncRunId],
        });
      }
    },
    retry: false,
  });
}

/**
 * SCH-4.2: trigger an immediate tenant-wide sync via POST /sync-now.
 *
 * Dispatches a full-catalog auto-continue run via the same _run_backfill_task
 * path used by scheduled cron ticks + start-backfill, but bypasses the
 * minute-grain pg_cron schedule. Use case: admin made a config change and
 * wants to verify against fresh data without waiting up to
 * `polling_interval_minutes`.
 *
 * On success, invalidates the active-backfill query so any UI that watches
 * "is a run in flight?" refreshes immediately.
 */
export function useSyncNow() {
  const queryClient = useQueryClient();
  return useMutation<
    BackfillMutationResult<{ syncRunId: string; status: 'started' }>,
    Error,
    void
  >({
    mutationFn: () => backfillProgressService.syncNow(),
    onSuccess: (result) => {
      if (result.data) {
        queryClient.invalidateQueries({ queryKey: ACTIVE_BACKFILL_KEY });
      }
    },
    retry: false,
  });
}

/**
 * SCH-4.5: paginated sync-history query for the Sync History tab.
 * Backed by the GET /sync-history endpoint. Refetches when filter/page
 * params change. No polling (admin reads on demand).
 */
export function useSyncHistory(options: {
  limit?: number;
  offset?: number;
  status?: import('@/types/integrations/backfillProgress').SyncHistoryStatus;
  type?: import('@/types/integrations/backfillProgress').SyncHistoryType;
} = {}) {
  return useQuery({
    queryKey: [
      'sch',
      'sync-history',
      options.limit ?? 25,
      options.offset ?? 0,
      options.status ?? 'all',
      options.type ?? 'all',
    ],
    queryFn: () => backfillProgressService.getSyncHistory(options),
    staleTime: 10_000, // 10s — admin views infrequently but might filter rapidly
  });
}

/**
 * Polling query for one sync_run_id's progress.
 *
 * - 3s refetchInterval while the run is actively progressing
 * - polls stop automatically when status flips to completed / paused / failed
 *   (a paused run still surfaces in the UI but isn't actively writing rows;
 *    polling adds no value)
 * - disabled when syncRunId is null (modal not yet showing progress mode)
 */
export function useSyncProgress(syncRunId: string | null) {
  return useQuery<BackfillMutationResult<SyncProgressResponse>>({
    queryKey: [...SYNC_PROGRESS_KEY, syncRunId],
    queryFn: () => {
      if (!syncRunId) {
        return Promise.resolve({
          data: null,
          error: { code: 'internal_error' as const, message: 'No sync_run_id' },
        });
      }
      return backfillProgressService.getSyncProgress(syncRunId);
    },
    enabled: !!syncRunId,
    refetchInterval: (query) => {
      const result = query.state.data;
      if (!result || result.error) return false;
      const status = result.data?.status;
      if (
        status === 'completed' ||
        status === 'failed' ||
        status === 'paused'
      ) {
        return false;
      }
      return 3000;
    },
    // Stale-time low — we want every poll to actually hit the network.
    staleTime: 0,
    retry: false,
  });
}

/**
 * Returns the most-recent unfinished sync_run for this tenant_integration,
 * or null. Used by the SubContractorHubPanel to surface the Resume banner.
 *
 * Stale-time of 30s — the banner doesn't need to update second-by-second;
 * it's a coarse "is there an active run?" indicator.
 */
export function useActiveBackfill(options: { enabled?: boolean } = {}) {
  return useQuery<BackfillMutationResult<ActiveBackfillResponse | null>>({
    queryKey: ACTIVE_BACKFILL_KEY,
    queryFn: () => backfillProgressService.getActiveBackfill(),
    enabled: options.enabled ?? true,
    staleTime: 30 * 1000,
    retry: false,
  });
}

/**
 * SCH-3.5: returns the most-recent sync_run_id for this tenant_integration
 * — completed or in-progress, doesn't matter. Used by the "View last run"
 * button to open the progress modal read-only on a finished run.
 *
 * Direct Supabase query (RLS scopes by tenant). No backend endpoint needed
 * since the data is already exposed via tenant_integration_sync_history.
 */
export function useLatestBackfillRun(
  tenantIntegrationId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery<string | null>({
    queryKey: ['subcontractorhub-latest-backfill-run', tenantIntegrationId],
    queryFn: async () => {
      if (!tenantIntegrationId) return null;
      const { data, error } = await supabase
        .from('tenant_integration_sync_history')
        .select('sync_run_id')
        .eq('tenant_integration_id', tenantIntegrationId)
        .eq('phase', 'run_started')
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        console.error('Failed to fetch latest backfill run:', error);
        return null;
      }
      return data?.sync_run_id ?? null;
    },
    enabled: !!tenantIntegrationId && (options.enabled ?? true),
    staleTime: 30 * 1000,
  });
}
